import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

/**
 * Supabase Client — koneksi utama aplikasi EKINKINIAN ke backend Supabase.
 *
 * Environment variables (web/.env):
 *   VITE_SUPABASE_URL       — Project URL  (https://<project-ref>.supabase.co)
 *   VITE_SUPABASE_ANON_KEY  — Public anon key (aman untuk frontend, dilindungi RLS)
 *
 * Catatan: anon key sengaja dipublikasikan di frontend — keamanan data
 * dijamin oleh Row Level Security (RLS) di sisi database, bukan oleh
 * kerahasiaan key (lihat DC-12).
 */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/** True jika kedua env var Supabase sudah diset (dev .env atau Cloudflare Pages env). */
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

if (!isSupabaseConfigured) {
  // Jangan throw — cukup warning agar modul tetap bisa di-import.
  // Client dibuat dengan placeholder; auth calls akan gagal gracefully.
  console.warn(
    '[supabase] Environment variables VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY belum diset. ' +
      'Salin web/.env.example → web/.env (dev) atau set Environment Variables di Cloudflare Pages (production).',
  );
}

/**
 * Singleton client Supabase. Gunakan import ini di seluruh aplikasi:
 *
 *   import { supabase } from '$lib/supabase';
 *
 * Jika env vars belum diset, client dibuat dengan URL placeholder agar modul
 * tidak crash saat import. Auth/data calls akan gagal gracefully (network
 * error ke placeholder URL) — aplikasi tetap render, memungkinkan UI
 * menampilkan pesan konfigurasi yang informatif.
 */
export const supabase = createClient<Database>(
  supabaseUrl ?? 'https://placeholder.supabase.co',
  supabaseAnonKey ?? 'placeholder-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);
